import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Send, Loader2, Trash2, CornerDownRight, X, Edit3, Check, Eraser } from 'lucide-react';
import { getTierBadge, effectivePlan } from '../utils/tierBadge';
import { redactSelection } from '../utils/redactSelection';
import Redacted from './Redacted';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_VISUAL_DEPTH = 3;

const relTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const seconds = Math.floor((Date.now() - d.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const mins = Math.floor(seconds / 60);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * Renders a single comment card. Non-recursive by design - the parent walker
 * flattens the tree and passes each row here with its computed depth.
 */
function CommentRow(props) {
  const {
    comment,
    depth,
    onToggleLike,
    onReply,
    onDelete,
    activeReplyId,
    setActiveReplyId,
    replyDraft,
    setReplyDraft,
    replyAuthor,
    setReplyAuthor,
    submittingReplyFor,
    adminToken,
    user,
  } = props;

  const visualDepth = Math.min(depth, MAX_VISUAL_DEPTH);
  const indentPx = visualDepth * 20;
  const isReplyOpen = activeReplyId === comment.id;
  const [editing, setEditing] = React.useState(false);
  const [editDraft, setEditDraft] = React.useState(comment.comment_text || '');
  const [savingEdit, setSavingEdit] = React.useState(false);
  const [editErr, setEditErr] = React.useState('');
  const editTextareaRef = React.useRef(null);

  const submitReply = async (e) => {
    e.preventDefault();
    if (!replyDraft.trim() || submittingReplyFor === comment.id) return;
    await onReply(comment.id);
  };

  const saveEdit = async () => {
    if (!editDraft.trim()) return;
    setSavingEdit(true); setEditErr('');
    try {
      const res = await fetch(`${API}/api/admin/article-comments/${comment.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ comment_text: editDraft }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Save failed');
      comment.comment_text = editDraft; // optimistic local update
      setEditing(false);
    } catch (e) {
      setEditErr(e.message);
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div
      style={{ marginLeft: indentPx }}
      data-testid={'article-comment-node-' + comment.id}
    >
      <div
        className={
          'rounded-lg border bg-zinc-950/50 p-3.5 ' +
          (depth > 0 ? 'border-red-900/30 border-l-2 border-l-red-500/60' : 'border-zinc-800')
        }
        data-testid={'article-comment-' + comment.id}
      >
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          {depth > 0 && (
            <CornerDownRight className="h-3.5 w-3.5 text-red-500/60 shrink-0" aria-hidden />
          )}
          {comment.author_avatar_url ? (
            <img
              src={comment.author_avatar_url}
              alt=""
              data-testid={'article-comment-avatar-' + comment.id}
              className="h-6 w-6 rounded-full object-cover border border-zinc-700 shrink-0"
            />
          ) : null}
          {(() => {
            const badge = getTierBadge(effectivePlan(comment));
            return (
              <span
                data-testid={'article-comment-author-' + comment.id}
                className={'text-sm font-black truncate ' + badge.nameClass}
                style={badge.nameStyle || undefined}
                title={badge.label}
              >
                {badge.emoji ? (
                  <span
                    aria-hidden
                    data-testid={'article-comment-tier-emoji-' + comment.id}
                    className={'mr-1 ' + badge.emojiClass}
                  >
                    {badge.emoji}
                  </span>
                ) : null}
                {comment.author_name}
              </span>
            );
          })()}
          <span className="text-[10px] text-gray-500 uppercase tracking-widest">
            {relTime(comment.created_at)}
          </span>
          {comment.status === 'pending' && (
            <span className="text-[9px] font-black tracking-widest uppercase text-yellow-400 bg-yellow-500/10 border border-yellow-500/40 px-1.5 py-0.5 rounded">
              awaiting review
            </span>
          )}
          {adminToken && !editing && (
            <button
              type="button"
              onClick={() => { setEditing(true); setEditDraft(comment.comment_text || ''); }}
              className="text-gray-500 hover:text-yellow-400 ml-auto"
              aria-label="Edit / redact comment"
              data-testid={'article-comment-admin-edit-' + comment.id}
              title="Edit or redact this comment (type XXXX where you want a whiteout strip)"
            >
              <Edit3 className="h-3.5 w-3.5" />
            </button>
          )}
          {adminToken && (
            <button
              type="button"
              onClick={() => onDelete(comment)}
              className={editing ? 'ml-auto text-gray-500 hover:text-red-400' : 'text-gray-500 hover:text-red-400'}
              aria-label="Delete comment"
              data-testid={'article-comment-admin-delete-' + comment.id}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {editing ? (
          <div className="space-y-2">
            <textarea
              ref={editTextareaRef}
              value={editDraft}
              onChange={(e) => setEditDraft(e.target.value)}
              rows={3}
              maxLength={2000}
              data-testid={'article-comment-edit-textarea-' + comment.id}
              className="w-full bg-black border border-yellow-700/60 focus:border-yellow-400 rounded p-2 text-sm text-white outline-none resize-y"
            />
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => {
                  const next = redactSelection(editTextareaRef.current, editDraft);
                  if (next != null) { setEditDraft(next); setEditErr(''); }
                  else setEditErr('Highlight the word or name you want to redact first.');
                }}
                data-testid={'article-comment-edit-redact-' + comment.id}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-zinc-900 hover:bg-zinc-800 border border-yellow-700/50 hover:border-yellow-500 text-yellow-300 text-[10px] font-black uppercase tracking-widest"
                title="Highlight a name then click to whiteout it"
              >
                <Eraser className="h-3 w-3" /> Redact selection
              </button>
              <p className="text-[10px] text-yellow-300/70">
                or type <span className="font-mono font-black">XXXX</span> manually
              </p>
            </div>
            {editErr && <p className="text-[11px] text-red-400">{editErr}</p>}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={saveEdit}
                disabled={savingEdit || !editDraft.trim()}
                data-testid={'article-comment-edit-save-' + comment.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:bg-zinc-800 disabled:text-gray-500 text-white text-[10px] font-black uppercase tracking-widest"
              >
                {savingEdit ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
                Save
              </button>
              <button
                type="button"
                onClick={() => { setEditing(false); setEditErr(''); setEditDraft(comment.comment_text || ''); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-[10px] font-black uppercase tracking-widest"
              >
                <X className="h-3 w-3" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <p className="text-sm text-gray-200 whitespace-pre-wrap break-words">
            <Redacted text={comment.comment_text} testId={'article-comment-body-' + comment.id} />
          </p>
        )}

        <div className="mt-2 flex items-center gap-4">
          <button
            type="button"
            onClick={() => onToggleLike(comment)}
            data-testid={'article-comment-like-' + comment.id}
            aria-pressed={comment.user_liked}
            className={
              'inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest transition-colors ' +
              (comment.user_liked ? 'text-red-400' : 'text-gray-500 hover:text-red-300')
            }
          >
            <Heart className={'h-3.5 w-3.5 ' + (comment.user_liked ? 'fill-red-400' : '')} />
            <span>{comment.likes || 0}</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveReplyId(isReplyOpen ? null : comment.id)}
            data-testid={'article-comment-reply-btn-' + comment.id}
            className={
              'inline-flex items-center gap-1 text-xs font-bold uppercase tracking-widest transition-colors ' +
              (isReplyOpen ? 'text-red-400' : 'text-gray-500 hover:text-red-300')
            }
          >
            {isReplyOpen ? <X className="h-3.5 w-3.5" /> : <CornerDownRight className="h-3.5 w-3.5" />}
            <span>{isReplyOpen ? 'Cancel' : 'Reply'}</span>
          </button>
        </div>

        {isReplyOpen && (
          <form
            onSubmit={submitReply}
            className="mt-3 rounded border border-red-900/30 bg-black/40 p-3"
            data-testid={'article-comment-reply-form-' + comment.id}
          >
            {user ? (
              <div
                data-testid={'article-comment-reply-identity-' + comment.id}
                className="flex items-center gap-2 rounded border border-zinc-800 bg-black/40 px-2.5 py-1.5 mb-2"
              >
                {user.avatar_url ? (
                  <img src={user.avatar_url} alt="" className="h-6 w-6 rounded-full object-cover border border-zinc-700 shrink-0" />
                ) : (
                  <div className="h-6 w-6 rounded-full bg-zinc-900 border border-zinc-700 flex items-center justify-center text-[10px] font-black text-red-400 shrink-0">
                    {(user.display_name || 'O').slice(0, 1).toUpperCase()}
                  </div>
                )}
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">As</span>
                <span className="text-xs font-black text-white truncate flex-1">{user.display_name || 'Outlaw Reader'}</span>
                <Link
                  to="/account"
                  className="text-[10px] font-black uppercase tracking-[0.2em] text-red-400 hover:text-red-300 shrink-0"
                >
                  Change →
                </Link>
              </div>
            ) : (
              <input
                type="text"
                maxLength={60}
                value={replyAuthor}
                onChange={(e) => setReplyAuthor(e.target.value)}
                placeholder="Name (or leave blank for Anon)"
                data-testid={'article-comment-reply-author-' + comment.id}
                className="w-full bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded px-2.5 py-1.5 text-xs text-white outline-none"
              />
            )}
            <textarea
              maxLength={1000}
              required
              rows={2}
              value={replyDraft}
              onChange={(e) => setReplyDraft(e.target.value)}
              placeholder={'Replying to ' + comment.author_name + '…'}
              data-testid={'article-comment-reply-text-' + comment.id}
              className="w-full mt-1.5 bg-zinc-900 border border-zinc-800 focus:border-red-500 rounded px-2.5 py-1.5 text-xs text-white outline-none resize-y"
            />
            <div className="mt-1.5 flex items-center justify-between">
              <span className="text-[10px] text-gray-500 uppercase tracking-widest">
                {replyDraft.length}/1000
              </span>
              <button
                type="submit"
                disabled={submittingReplyFor === comment.id || !replyDraft.trim()}
                data-testid={'article-comment-reply-submit-' + comment.id}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded bg-red-600 hover:bg-red-500 disabled:bg-zinc-800 disabled:text-gray-500 text-white text-[10px] font-black uppercase tracking-widest"
              >
                {submittingReplyFor === comment.id ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Send className="h-3 w-3" />
                )}
                Post Reply
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default CommentRow;
